import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, X, Send, Loader2 } from 'lucide-react'
import { Button } from '@brix/ui'
import { tenantCanUseAiAssistant } from '@brix/types'
import { useAuthStore } from '@/store/authStore'
import { useEditorStore } from '@/store/editorStore'
import { useAiAssistantChat, type AiChatMessage } from '@/hooks/useAiAssistant'
import { summarizeBlocksForAi } from '@/lib/aiAssistantSummary'

const MAX_HISTORY = 6

interface AiAssistantPanelProps {
  siteId: string
  pageId: string
  onClose: () => void
}

export function AiAssistantPanel({ siteId, pageId, onClose }: AiAssistantPanelProps) {
  const tenant = useAuthStore((s) => s.tenant)
  const canUseAi = !!tenant && tenantCanUseAiAssistant(tenant)
  const blocks = useEditorStore((s) => s.blocks)
  const chat = useAiAssistantChat()

  const [messages, setMessages] = useState<AiChatMessage[]>([])
  const [input, setInput] = useState('')

  async function handleSend() {
    const message = input.trim()
    if (!message || chat.isPending) return
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: message }])

    try {
      const result = await chat.mutateAsync({
        siteId,
        pageId,
        message,
        context: summarizeBlocksForAi(blocks),
        history: messages.slice(-MAX_HISTORY),
      })
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply }])
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: e instanceof Error ? e.message : 'Something went wrong.' }])
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <h2 className="text-sm font-semibold text-gray-900">AI Assistant</h2>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!canUseAi ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <Sparkles className="h-6 w-6 text-violet-300" />
          <p className="text-sm text-gray-500">
            Chat with AI to edit this page — describe a change and it applies it directly to the canvas.
          </p>
          <Link
            to="/billing"
            className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-600 hover:bg-amber-100 transition-colors"
          >
            Upgrade to Pro
          </Link>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <p className="text-xs text-gray-400">
                Try “make the hero background darker” or “add a pricing section after the features”.
              </p>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.role === 'user'
                    ? 'ml-auto bg-violet-600 text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {m.content}
              </div>
            ))}
            {chat.isPending && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Thinking…
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-3">
            <input
              type="text"
              aria-label="Describe a change to the page"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSend() }}
              placeholder="Describe a change…"
              maxLength={500}
              disabled={chat.isPending}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60"
            />
            <Button onClick={() => void handleSend()} disabled={!input.trim() || chat.isPending} size="sm" aria-label="Send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
